import React, { useState, useMemo } from 'react';
import { Row, Col, Card, CardBody, Table, Badge, Input, Label, Button } from 'reactstrap';
import Chart from 'react-apexcharts';
import moment from 'moment';
import formatDate from 'constants/formatDate';

const TransactionAnalysis = ({ transactions }) => {
    // State for filters
    const [filters, setFilters] = useState({
        paid: true,
        unpaid: true,
        product: 'All'
    });

    // Handle Filter Change
    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? !checked : value
        }));
    };

    // Filter Data & Calculate Aggregates
    // Filter Data & Calculate Aggregates
    const { filteredData, totalAmount, totalCount, chartData, uniqueProducts } = useMemo(() => {
        if (!transactions) return { filteredData: [], totalAmount: 0, totalCount: 0, chartData: {}, uniqueProducts: [] };

        // Get Unique Products for Select Box
        const uniqueProducts = ['All', ...new Set(transactions.map(t => t.product_name).filter(Boolean))];

        // 1. Filter
        const data = transactions.filter(item => {
            // Product Filter
            if (filters.product !== 'All' && item.product_name !== filters.product) return false;

            // Status Filter
            if (filters.paid && item.payment_status === 'paid') return true;
            if (filters.unpaid && item.payment_status === 'unpaid') return true;
            return false;
        });

        // 2. Aggregates
        let sum = 0;
        data.forEach(item => {
            sum += parseFloat(item.paid_amount || 0);
        });

        // 3. Prepare Chart Data (Group by Date)
        const dateMap = {};
        data.forEach(item => {
            const dateStr = moment(item.purchase_date).format('YYYY-MM-DD');
            if (!dateMap[dateStr]) dateMap[dateStr] = 0;
            dateMap[dateStr] += 1;
        });

        // Sort dates for chart
        const sortedDates = Object.keys(dateMap).sort();
        const seriesData = sortedDates.map(date => dateMap[date]);

        return {
            filteredData: data,
            totalAmount: sum,
            totalCount: data.length,
            chartData: {
                categories: sortedDates,
                series: [{ name: 'Transactions', data: seriesData }]
            },
            uniqueProducts
        };
    }, [transactions, filters]);

    // Chart Options
    const chartOptions = {
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        colors: ['#34c38f'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3 },
        grid: {
            borderColor: '#f1f1f1',
            strokeDashArray: 3,
        },
        xaxis: {
            categories: chartData.categories,
            labels: { style: { colors: '#fff' } }
        },
        yaxis: {
            labels: { style: { colors: '#fff' } }
        },
        tooltip: {
            theme: 'light',
            style: {
                color: '#000000'
            }
        }
    };

    // Export Function
    const handleExport = () => {
        if (!filteredData || filteredData.length === 0) return;

        const headers = ["Date", "Name", "Email", "Phone", "Product", "Amount", "Status"];

        const csvRows = filteredData.map(row => {
            const escape = (text) => `"${String(text || "").replace(/"/g, '""')}"`;
            return [
                escape(moment(row.purchase_date).format('YYYY-MM-DD HH:mm:ss')),
                escape(row.customer_name),
                escape(row.email),
                escape(row.phone),
                escape(row.product_name),
                row.paid_amount,
                escape(row.payment_status)
            ].join(",");
        });

        const csvContent = [headers.join(","), ...csvRows].join("\n");
        const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Transactions_${moment().format('YYYY-MM-DD')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <React.Fragment>
            {/* --- FILTERS & SUMMARY --- */}
            <Row className="mb-3 align-items-center" style={{ paddingLeft: 20, paddingRight: 20 }}>
                <Col md={6} className="d-flex gap-4 align-items-center">
                    <div className="d-flex gap-3 align-items-center">
                        <Input
                            type="select"
                            name="product"
                            value={filters.product}
                            onChange={handleFilterChange}
                            className="form-select form-select-sm"
                            style={{ maxWidth: '200px' }}
                        >
                            {uniqueProducts && uniqueProducts.map((prod, idx) => (
                                <option key={idx} value={prod}>{prod}</option>
                            ))}
                        </Input>
                    </div>

                    <div className="form-check form-check-inline">
                        <Input
                            type="checkbox"
                            className="form-check-input"
                            id="filterPaid"
                            name="paid"
                            checked={filters.paid}
                            onClick={handleFilterChange}
                        />
                        <Label className="form-check-label text-white" htmlFor="filterPaid">
                            Show Paid
                        </Label>
                    </div>
                    <div className="form-check form-check-inline">
                        <Input
                            type="checkbox"
                            className="form-check-input"
                            id="filterUnpaid"
                            name="unpaid"
                            checked={filters.unpaid}
                            onClick={handleFilterChange}
                        />
                        <Label className="form-check-label text-white" htmlFor="filterUnpaid">
                            Show Unpaid
                        </Label>
                    </div>

                    <Button
                        color="success"
                        size="sm"
                        onClick={handleExport}
                    >
                        <i className="bx bx-download me-1"></i> Export
                    </Button>
                </Col>
                <Col md={6} className="text-end text-white">
                    <span className="me-3">
                        Total Transactions: <strong>{totalCount}</strong>
                    </span>
                    <span>
                        Total Revenue: <strong>${totalAmount.toFixed(2)}</strong>
                    </span>
                </Col>
            </Row>

            {/* --- CONTENT ROW --- */}
            <Row>
                {/* --- LEFT COL: TABLE --- */}
                <Col xl={6}>
                    <Card style={{ backgroundColor: "#3C3C3C", height: '400px', overflow: 'hidden' }}>
                        <CardBody style={{ overflowY: 'auto', maxHeight: '100%' }}>
                            <h5 className="text-white mb-3">Transaction List</h5>
                            <div className="table-responsive">
                                <Table className="table mb-0 text-white table-hover table-dark">
                                    <thead className='table-success'>
                                        <tr>
                                            <th>Date</th>
                                            <th>Name</th>
                                            <th>Product</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((item, index) => (
                                            <tr key={index}>
                                                <td>{formatDate(item.purchase_date)}</td>
                                                <td>
                                                    {item.customer_name !== 'N/A' ? item.customer_name : 'Guest'}
                                                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{item.licence_plate}</div>
                                                </td>
                                                <td style={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {item.product_name}
                                                </td>
                                                <td>${item.paid_amount}</td>
                                                <td>
                                                    <Badge
                                                        color={item.payment_status === 'paid' ? 'success' : 'danger'}
                                                        className="font-size-12"
                                                    >
                                                        {item.payment_status.toUpperCase()}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredData.length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="text-center">No transactions found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                {/* --- RIGHT COL: GRAPH --- */}
                <Col xl={6}>
                    <Card style={{ backgroundColor: "#3C3C3C", height: '400px' }}>
                        <CardBody>
                            <h5 className="text-white mb-3">Transaction Volume</h5>
                            <Chart
                                options={chartOptions}
                                series={chartData.series}
                                type="area"
                                height={320}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default TransactionAnalysis;